// disable useContraMutation eslint error

import { type AdminSignInUrlMutation } from '#app/__generated__/relay/AdminSignInUrlMutation.graphql.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { adminSignInUrl } from '#app/features/admin/mutations/AdminSignInUrl.js';
import { useCopyToClipboard } from '#app/hooks/useCopyToClipboard.js';
import { useHasUserFeatures } from '#app/hooks/useHasUserFeatures.js';
import { parseJson } from '#app/utilities/parseJson.js';
import { useMutation } from '@contra/relay';
import { decodeBase64 } from '@contra/utilities/decodeBase64';
import { encodeBase64 } from '@contra/utilities/encodeBase64';
import { safeStringify } from '@contra/utilities/safeStringify';
import { useCallback } from 'react';

export const ImpersonateUser = ({
  userProfileId,
}: {
  readonly userProfileId: string;
}) => {
  const toast = useToast();
  const copyToClipboard = useCopyToClipboard();
  const hasPermissions = useHasUserFeatures(['IMPERSONATE_USERS']);

  const [getAdminSignInUrl] =
    useMutation<AdminSignInUrlMutation>(adminSignInUrl);

  const getAdminUserSearchResultId = useCallback((): string => {
    return encodeBase64(
      safeStringify([
        'AdminUserSearchResult',
        ...parseJson(decodeBase64(userProfileId)).slice(1),
      ]),
    );
  }, [userProfileId]);

  const adminImpersonateUser = async () => {
    getAdminSignInUrl({
      onCompleted: (data) => {
        const { signInUrl } = data.adminCreateSignInUrl;

        if (signInUrl) {
          void copyToClipboard(signInUrl, {
            successToast: 'Sign in URL copied to clipboard',
          });
        } else {
          toast.warning('Did not receive the login link. Try again');
        }
      },
      onError: () => {
        toast.error('An error occurred. Try again');
      },
      variables: {
        input: {
          id: getAdminUserSearchResultId(),
          sendShortCode: true,
        },
      },
    });
  };

  if (!hasPermissions) return null;

  return (
    <button
      id="impersonate-user-button"
      onClick={adminImpersonateUser}
      title="Impersonate user"
      type="button"
    >
      😏 Impersonate
    </button>
  );
};
